import useNavigationStore from '@src/stores/navigationStore';
import useFilesStore from '@src/stores/filesStore';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';

import toast from 'react-hot-toast';
import useRegistrationStore from '@src/stores/registrationStore';
import InfoBlueBox from '@src/components/_shared/InfoBlueBox';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fileCheck, getAllDocumentsData } from '@src/api/Registration';
import DocumentStatus from '@src/components/_shared/DocumentStatus';
import Loader from '@src/components/_shared/UI/Loader';
import { DocumentStatusDataType, DocumentStatusObjectType, StatusType, DocumentType, DocumentLabelType } from '@src/utilities/types';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { useState } from 'react';
import { MAXSIZEFILE } from '@src/utilities/constants';
import { handleApiError } from '@src/utilities/functions';
import useAuthStore from '@src/stores/authStore';

export default function Overview() {
    const { setBlock } = useNavigationStore();
    const { registration } = useRegistrationStore();
    const { isLoginModalOpen } = useAuthStore();
    const { addLegalRepresentativeFile, removeLegalRepresentativeFile } = useFilesStore();
    const [loading, setLoading] = useState<boolean>(false);

    const { data, isLoading, refetch } = useQuery<DocumentStatusDataType>({ 
        queryKey: ["documentsStatus"],
        queryFn: () => getAllDocumentsData(registration.partner_informations.is_partner ? registration.demand_id : undefined),
        refetchInterval: 10000,
        enabled: !isLoginModalOpen
    });

    const { mutate, isPending } = useMutation({
        mutationFn: ({type, newFileFormData}: {type: DocumentType, newFileFormData: FormData}) => fileCheck(type, newFileFormData),
        onSuccess: () => {
            setLoading(false);
            refetch();
        },
        onError: (error: any) => {
            let errorMessage = `Une erreur est survenue lors de la validation du document.`;
            if (error?.response?.status === 400) {
                errorMessage = 'Le document est invalide. Veuillez en sélectionner un autre.';
            }
            if (error?.response?.status === 409) {
                errorMessage = 'Ce document existe déjà. Veuillez en sélectionner un autre.';
            }
            handleApiError(error, errorMessage);
            setLoading(false);
        }
    });

    const _onChangeDocument = (newFile: File, document: DocumentStatusObjectType, type: DocumentType) => {
        setLoading(true);
        if (!newFile || !(newFile instanceof File)) {
            return toast.error("Le fichier recto téléchargé est invalide. Veuillez réessayer.");
        }
        if (newFile.size > MAXSIZEFILE) {
            return toast.error('Ce fichier est trop volumineux. La taille maximum est de 10 Mo.', { position: 'top-center'});
        }

        const newFileObjet = {
            id: type === DocumentType.front ? 1 : type === DocumentType.back ? 2 : 3,
            fileName: newFile.name,
            fileSize: newFile.size,
            file: newFile,
            type: document.type,
            type_detail: document.type_detail,
            label: newFile.type === 'application/pdf' ? DocumentLabelType.pdf : DocumentLabelType.image
        }
        removeLegalRepresentativeFile(type === DocumentType.front ? 1 : type === DocumentType.back ? 2 : 3);
        addLegalRepresentativeFile(newFileObjet);

        const newFileFormData = new FormData();
        newFileFormData.append('file', newFile);
        if (newFileObjet.type_detail) newFileFormData.append('type_detail', newFileObjet.type_detail);
        newFileFormData.append('demand_id', registration.demand_id);
        newFileFormData.append('fromWeb', 'true');
        newFileFormData.append('registrationType', 'company');
        if (registration.partner_informations.is_partner) newFileFormData.append('is_psi', 'true');
        if (registration.person_id) newFileFormData.append('person_id', registration.person_id);
        if (registration.partner_informations.brand.slug) newFileFormData.append('brand', registration.partner_informations.brand.slug);
        mutate({type, newFileFormData});
    }

    const renderInfoBox = (data: DocumentStatusDataType) => {
        if (data && !data.signable) {
            return (
                <InfoBlueBox
                    boxTitle="Attention !"
                    description="Un ou plusieurs documents nécessitent une attention particulière, un email vous sera envoyé lorsque ceux-ci auront été validés. En cas de refus d'un document, un email vous sera envoyé."
                />
            );
        }
        return null;
    };
    
    const renderDocuments = (data: DocumentStatusDataType, onChangeDocument: (newFile: File, document: DocumentStatusObjectType, type: DocumentType) => void) => {
        return data.documents.map((document: DocumentStatusObjectType) => {
            const { filename, status, message, type } = document;
            return (
                <DocumentStatus
                    key={type}
                    modifyFile={(newFile: File) => onChangeDocument(newFile, document, type as DocumentType)}
                    fileName={filename}
                    status={status as StatusType}
                    message={message}
                    type={type as DocumentType}
                />
            );
        });
    };
    
    const renderContent = (data: DocumentStatusDataType | undefined, isLoading: boolean, isPending: boolean, loading: boolean, onChangeDocument: any, setBlock: any) => {
        if (isLoading || isPending || loading) {
            return <Loader type='SPINNER' />;
        }
        if (!data) return null;
        return (
            <>
                {renderInfoBox(data)}
                {renderDocuments(data, onChangeDocument)}
                <ValidateButton 
                    title="Procéder à la signature électronique" 
                    disabled={isLoading || !data.signable}
                    onClick={() => setBlock('Signature')} 
                />
            </>
        );
    };

    return (
        <Container>
            <BlockTitle title="Documents nécessaires" />
            <Subtitle content="Téléchargez les documents requis pour terminer votre ouverture de compte et commencer à investir" />
            {renderContent(data, isLoading, isPending, loading, _onChangeDocument, setBlock)}
        </Container>
    );
}
